import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import Swal from 'sweetalert2';
import SVG from 'react-inlinesvg';
import { Rating as Stars } from '@mui/material';

import Text from '../../../../shared/Text';
import TextInput from '../../../../shared/FormStyles/TextInput.jsx';
import { Button } from '../../../../shared/Button/Button';
import Close from '../../../../assets/Close.svg';
import { useNotification } from '../../../../shared/Notification/useNotification';
import {
  useCreateUserFeedbackMutation,
  useUpdateUserFeedbackMutation,
} from './hooks/useCampaignUserFeedbackMutation';

const Rating = ({
  campaignUuid,
  campaignName,
  isAdmin = false,
  onClose,
  onStarRating,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('campaigns');
  const { showSuccess } = useNotification();
  const [starRating, setStarRating] = useState('');
  const [textRating, setTextRating] = useState('');
  const [feedbackId, setFeedbackId] = useState(null);
  const { createUserFeedbackMutation } = useCreateUserFeedbackMutation(
    setFeedbackId,
  );
  const { updateUserFeedbackMutation } = useUpdateUserFeedbackMutation(onClose);

  const starRatingChanged = (event, rate) => {
    createUserFeedbackMutation({ rate, campaignUuid, isAdmin });
    setStarRating(rate);
    if (onStarRating) {
      onStarRating();
    }
  };

  const textRatingChanged = message => {
    setTextRating(message.target.value);
  };

  const closeAndNotify = () => {
    if (onClose) {
      onClose();
    } else {
      Swal.close();
    }
    showSuccess({
      content: t('feedback.notifySuccess'),
    });
  };

  const starsRatingContainer = (
    <React.Fragment>
      <Text variant="h6" m="10px 0 0">
        {isAdmin ? (
          <>
            🎉{' '}
            {t('feedback.admin.wrappedUp', {
              campaignName,
            })}
          </>
        ) : (
          <>🎉 {t('feedback.user.congrats')}</>
        )}
      </Text>
      <Body>
        <Text variant="subtitle2" as="a">
          {isAdmin ? t('feedback.admin.rate') : t('feedback.user.rate')}
        </Text>
        <StarsWrapper>
          <Stars
            onChange={starRatingChanged}
            icon={
              <i
                className="fa fa-star"
                style={{ color: theme.colors.primary['100'], fontSize: '32px' }}
              ></i>
            }
            emptyIcon={
              <i
                className="fa fa-star"
                style={{ color: theme.colors.black['20'], fontSize: '32px' }}
              ></i>
            }
          />
        </StarsWrapper>
      </Body>
    </React.Fragment>
  );

  const textRatingContainer = (
    <React.Fragment>
      <CloseButton src={Close} onClick={() => closeAndNotify()} />
      <Title>
        <Text variant="h6">
          {isAdmin
            ? t('feedback.admin.tellUsMore')
            : t('feedback.user.tellUsMore')}
        </Text>
      </Title>
      <Body>
        <TextInput
          name="feedbackMessage"
          as="textarea"
          height="100px"
          width="426px"
          m="10px 0 10px"
          charactersLimit={300}
          value={textRating}
          onChange={textRatingChanged}
          placeholder={isAdmin ? t('feedback.admin.textPlaceholder') : ''}
        />
        <Button
          mode="contained"
          variant="primary"
          m="15px 0 10px"
          onClick={() => updateUserFeedbackMutation({ feedbackId, textRating })}
        >
          {t('feedback.submit')}
        </Button>
      </Body>
    </React.Fragment>
  );

  return (
    <FormWrapper>
      {starRating ? textRatingContainer : starsRatingContainer}
    </FormWrapper>
  );
};

export default Rating;

Rating.propTypes = {
  campaignUuid: PropTypes.string,
  campaignName: PropTypes.string,
  isAdmin: PropTypes.bool,
  onClose: PropTypes.func,
  onStarRating: PropTypes.func,
};

const FormWrapper = styled.div`
  width: 100%;
  height: 250px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
`;

const StarsWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0 10px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  width: 100%;
`;

const Title = styled.div`
  align-self: flex-start;
  margin-top: 10px;
`;

const CloseButton = styled(SVG)`
  position: absolute;
  top: 13px;
  right: 13px;
  cursor: pointer;
  width: 20px;
  height: 20px;

  path {
    stroke: ${themeGet('colors.black.80')};
  }
`;
